import { Button } from 'primereact/button';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import PenFilledIcon from '../../../../../../../../../../../../assets/icons/PenFilled';
import { IManualInputMaterialData } from '../../../../../../../../../../../../components/dialog-manager/dialogs/enter-manual-input-material-dialog/EnterManualInputMaterialDialog';
import ErrorMessage from '../../../../../../../../../../../../components/error-message';
import useDialogContext from '../../../../../../../../../../../../hooks/dialog/useDialogContext';
import CustomIcon from '../../../../../../../../../../../../shared/components/custom-icon/CustomIcon';
import { IComponent } from '../../../../../../../../../../../../shared/interfaces/IComponent';
import {
  mapComponentToInputMaterial,
  mapManualDataToInputMaterial,
} from '../input-materials-section/helpers';
import SelectedInputMaterialDetail from './components/selected-input-material-detail/SelectedInputMaterialDetail';

interface InputMaterialProps {
  formPath: string;
}

/**
 * The single input material of an Injection Molding process.
 *
 * Injection Molding keeps the old one-slot behaviour (`input_materials[0]`, process-level
 * sprue/scrap waste management) and was deliberately left out of PAC-1611/PAC-1615's move to
 * multiple materials per process, which only applies to Individual Process. That's why there
 * are two components: this one binds a single `useController` to a fixed slot, while Individual
 * Process uses `InputMaterialsSection`, which owns a `useFieldArray` and a card per material.
 */
const InputMaterial = ({ formPath }: InputMaterialProps): JSX.Element => {
  const { t } = useTranslation();
  const { openDialog } = useDialogContext();

  const { control } = useFormContext();
  const {
    field: { ref, onChange, value },
    fieldState: { error },
  } = useController({
    name: formPath,
    control,
  });

  const handleComponentSelect = (components: IComponent[]) => {
    const [component] = components;

    if (component) {
      onChange(mapComponentToInputMaterial(component));
    }
  };

  const handleManualAdd = (manualInputMaterialData: IManualInputMaterialData) => {
    onChange(mapManualDataToInputMaterial(manualInputMaterialData));
  };

  const onRemoveInputMaterial = () => {
    onChange(null);
  };

  return (
    <div className='surface-ground border-round-2xl p-3 mb-3'>
      <div className='flex flex-row w-full justify-content-between align-items-center'>
        <h3 className='mb-3 font-medium' style={{ fontSize: '16px' }}>
          {t('footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.title')}
        </h3>
      </div>
      {!value ? (
        // If nothing is selected, show buttons to select or enter manually
        <div>
          <div className='flex align-items-center gap-4'>
            {/* Select Component button */}
            <Button
              label={t(
                'footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.selectComponentOrText',
              )}
              className='p-button-primary'
              icon='pi pi-plus-circle'
              iconPos='left'
              pt={{
                icon: {
                  className: 'text-xl',
                },
              }}
              onClick={() => {
                openDialog('select-input-materials-dialog', {
                  mode: 'single',
                  onSelect: handleComponentSelect,
                });
              }}
            />

            <span className='font-sm text-gray-400'>
              {t('footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.orText')}
            </span>

            {/* Enter Manually button */}
            <div
              className='p-button p-0 p-button-text flex flex-row align-items-center'
              onClick={() => {
                openDialog('enter-manual-input-material-dialog', { onAdd: handleManualAdd });
              }}
            >
              <CustomIcon icon={PenFilledIcon} className='flex w-10 p-0 h-full' />
              <span className='text-primary font-medium text-center h-full'>
                {t(
                  'footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.enterManuallyText',
                )}
              </span>
            </div>
          </div>
          <div>
            {/* Error Message Block for Input Materials Required Error Message */}
            {error?.message && (
              <div className='py-2'>
                <ErrorMessage message={error.message} />
              </div>
            )}
          </div>
        </div>
      ) : (
        // If a component is selected, show their details
        <div className='p-3 border-round-2xl bg-dark-gray'>
          <SelectedInputMaterialDetail
            onRemoveInputMaterial={onRemoveInputMaterial}
            inputMaterial={value}
          />
        </div>
      )}

      {/* Hidden input to enable focus & auto-scroll on error */}
      <input readOnly className='opacity-0 p-0 m-0 w-0 h-0 absolute' ref={ref} />
    </div>
  );
};

export default InputMaterial;
